import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { Bath, BedDouble, Building2, Ruler, Store } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { localizePath } from "@/i18n/translations";
import { resolveImageUrl } from "@/lib/api";
import "leaflet/dist/leaflet.css";

const MAP_DEFAULT_CENTER = { lat: 21.4225, lng: 39.8262 };
const MAP_DEFAULT_ZOOM = 7;
const SHEET_MIN_HEIGHT = 18;
const SHEET_MAX_HEIGHT = 92;

const SUB_AREA_FALLBACK_COORDINATES = {
  "makkah-city": { lat: 21.4225, lng: 39.8262 },
  jeddah: { lat: 21.5433, lng: 39.1728 },
  taif: { lat: 21.2703, lng: 40.4158 },
  qunfudhah: { lat: 19.1262, lng: 41.0789 },
  lith: { lat: 20.1397, lng: 40.2666 },
  aziziyah: { lat: 21.4068, lng: 39.868 },
  "al-shatea": { lat: 21.641, lng: 39.1034 },
  "al-hada": { lat: 21.3669, lng: 40.2865 },
  general: MAP_DEFAULT_CENTER,
};

const toSafeNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const normalized = value
    .toString()
    .trim()
    .replace(/[،]/g, ".")
    .replace(/,/g, ".");

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed;
};

const isInsideBounds = (point, bounds) => {
  if (!bounds) {
    return true;
  }

  const inLatitude = point.lat >= bounds.south && point.lat <= bounds.north;
  const inLongitude =
    bounds.west <= bounds.east
      ? point.lng >= bounds.west && point.lng <= bounds.east
      : point.lng >= bounds.west || point.lng <= bounds.east;

  return inLatitude && inLongitude;
};

const getSubAreaLabel = (item, lang) => {
  if (!item || typeof item !== "object") {
    return "";
  }

  if (typeof item.name === "string" && item.name.trim()) {
    return item.name.trim();
  }

  const label = item.name?.[lang] || item.name?.ar || item.name?.en || item.code || "";
  return typeof label === "string" ? label : String(label);
};

const hasPositiveCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

const getUnitsLabel = (copy, propertyType) =>
  propertyType === "shop"
    ? copy.properties.shopsCount || copy.properties.unitsCount
    : copy.properties.unitsCount;

const createPricePinIcon = ({ label, isActive, markerId }) =>
  L.divIcon({
    className: "airbnb-price-pin-wrapper",
    html: `<span class="airbnb-price-pin ${isActive ? "is-active" : ""}" data-testid="home-map-marker-button-${markerId}">${label}</span>`,
    iconSize: [148, 40],
    iconAnchor: [74, 20],
  });

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  return L.divIcon({
    className: "airbnb-cluster-icon-wrapper",
    html: `<div class="airbnb-cluster-icon" data-testid="home-map-cluster-${count}"><span>${count}</span></div>`,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
  });
};

const MapViewportBridge = ({ onViewportChange, onMapReady }) => {
  const map = useMap();

  useEffect(() => {
    onMapReady(map);
    const timer = window.setTimeout(() => {
      map.invalidateSize();
      onViewportChange(map.getBounds());
    }, 140);

    return () => window.clearTimeout(timer);
  }, [map, onMapReady, onViewportChange]);

  useMapEvents({
    moveend(event) {
      onViewportChange(event.target.getBounds());
    },
    zoomend(event) {
      onViewportChange(event.target.getBounds());
    },
  });

  return null;
};

const PropertyPreview = ({ lang, property, copy, testIdPrefix, layout = "stack" }) => {
  if (!property) {
    return null;
  }

  const imageSource = resolveImageUrl(property.images?.[0]);
  const isDesktopDock = layout === "desktop-dock";

  return (
    <article
      className={`airbnb-map-preview-card ${isDesktopDock ? "airbnb-map-preview-card-desktop" : ""}`}
      data-testid={`${testIdPrefix}-container`}
    >
      <div
        className={`relative overflow-hidden rounded-2xl ${isDesktopDock ? "airbnb-map-preview-media-desktop" : "aspect-[16/9]"}`}
        data-testid={`${testIdPrefix}-image-wrapper`}
      >
        {imageSource ? (
          <img
            src={imageSource}
            alt={property.title}
            className="h-full w-full object-cover"
            loading="lazy"
            data-testid={`${testIdPrefix}-image`}
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-stone-200 to-stone-100" data-testid={`${testIdPrefix}-image-fallback`} />
        )}
      </div>

      <div className="space-y-2" data-testid={`${testIdPrefix}-content`}>
        <p className="text-lg font-semibold text-stone-900" data-testid={`${testIdPrefix}-title`}>{property.title}</p>
        <p className="text-sm text-stone-600" data-testid={`${testIdPrefix}-location`}>{property.location}</p>
        <p className="text-base font-semibold text-gold-700" data-testid={`${testIdPrefix}-price`}>
          {property.displayPrice}
        </p>

        <div className="flex flex-wrap items-center gap-3 text-xs text-stone-600" data-testid={`${testIdPrefix}-meta`}>
          {hasPositiveCount(property.bedrooms) && (
            <span className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-bedrooms`}>
              <BedDouble size={14} /> {property.bedrooms}
            </span>
          )}
          {hasPositiveCount(property.bathrooms) && (
            <span className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-bathrooms`}>
              <Bath size={14} /> {property.bathrooms}
            </span>
          )}
          {hasPositiveCount(property.floors_count) && (
            <span className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-floors`}>
              <Building2 size={14} /> {property.floors_count}
            </span>
          )}
          {hasPositiveCount(property.units_count) && (
            <span className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-units`}>
              <Store size={14} /> {property.units_count}
            </span>
          )}
          <span className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-area`}>
            <Ruler size={14} /> {property.area_sqm}
          </span>
        </div>

        {property.isApproximate && (
          <Badge variant="outline" className="border-gold-500/40 bg-amber-50 text-stone-700" data-testid={`${testIdPrefix}-approximate-badge`}>
            {copy.mapExperience.approximateLocation}
          </Badge>
        )}

        <Link to={localizePath(lang, `/properties/${property.id}`)} data-testid={`${testIdPrefix}-details-link`}>
          <Button className="w-full rounded-full bg-stone-900 text-cream-50 hover:bg-stone-700" data-testid={`${testIdPrefix}-details-button`}>
            {copy.mapExperience.viewDetails}
          </Button>
        </Link>
      </div>
    </article>
  );
};

const PropertyListCard = ({ lang, property, copy, isHighlighted, onFocus }) => {
  const imageSource = resolveImageUrl(property.images?.[0]);

  return (
    <article
      className={`rounded-3xl border bg-white/90 p-4 shadow-sm transition-all ${
        isHighlighted ? "border-gold-500/40 shadow-md" : "border-gold-500/20"
      }`}
      data-testid={`home-map-list-card-${property.id}`}
    >
      <button
        type="button"
        className="w-full text-start"
        onClick={onFocus}
        data-testid={`home-map-list-focus-button-${property.id}`}
      >
        <div className="grid gap-4 sm:grid-cols-[170px_minmax(0,1fr)]" data-testid={`home-map-list-card-grid-${property.id}`}>
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl" data-testid={`home-map-list-image-wrapper-${property.id}`}>
            {imageSource ? (
              <img
                src={imageSource}
                alt={property.title}
                className="h-full w-full object-cover"
                loading="lazy"
                data-testid={`home-map-list-image-${property.id}`}
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-stone-200 to-stone-100" data-testid={`home-map-list-image-fallback-${property.id}`} />
            )}
          </div>

          <div className="space-y-2" data-testid={`home-map-list-content-${property.id}`}>
            <h3 className="text-lg font-semibold text-stone-900" data-testid={`home-map-list-title-${property.id}`}>{property.title}</h3>
            <p className="line-clamp-2 text-sm text-stone-600" data-testid={`home-map-list-description-${property.id}`}>{property.description}</p>
            <p className="text-base font-semibold text-gold-700" data-testid={`home-map-list-price-${property.id}`}>{property.displayPrice}</p>

            <div className="flex flex-wrap items-center gap-3 text-xs text-stone-600" data-testid={`home-map-list-meta-${property.id}`}>
              {hasPositiveCount(property.bedrooms) && (
                <span className="inline-flex items-center gap-1" data-testid={`home-map-list-bedrooms-${property.id}`}>
                  <BedDouble size={14} /> {property.bedrooms}
                </span>
              )}
              {hasPositiveCount(property.bathrooms) && (
                <span className="inline-flex items-center gap-1" data-testid={`home-map-list-bathrooms-${property.id}`}>
                  <Bath size={14} /> {property.bathrooms}
                </span>
              )}
              {hasPositiveCount(property.floors_count) && (
                <span className="inline-flex items-center gap-1" data-testid={`home-map-list-floors-${property.id}`}>
                  <Building2 size={14} /> {property.floors_count}
                </span>
              )}
              {hasPositiveCount(property.units_count) && (
                <span className="inline-flex items-center gap-1" data-testid={`home-map-list-units-${property.id}`}>
                  <Store size={14} /> {property.units_count}
                </span>
              )}
              <span className="inline-flex items-center gap-1" data-testid={`home-map-list-area-${property.id}`}>
                <Ruler size={14} /> {property.area_sqm}
              </span>
            </div>
          </div>
        </div>
      </button>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2" data-testid={`home-map-list-actions-${property.id}`}>
        <div className="flex items-center gap-2">
          {property.isApproximate && (
            <Badge variant="outline" className="border-gold-500/40 bg-amber-50 text-stone-700" data-testid={`home-map-list-approximate-badge-${property.id}`}>
              {copy.mapExperience.approximateLocation}
            </Badge>
          )}
          <Badge variant="outline" className="border-gold-500/30 bg-white text-stone-700" data-testid={`home-map-list-location-source-${property.id}`}>
            {property.locationLabel}
          </Badge>
        </div>

        <Link to={localizePath(lang, `/properties/${property.id}`)} data-testid={`home-map-list-details-link-${property.id}`}>
          <Button className="rounded-full bg-stone-900 px-5 text-cream-50 hover:bg-stone-700" data-testid={`home-map-list-details-button-${property.id}`}>
            {copy.mapExperience.viewDetails}
          </Button>
        </Link>
      </div>
    </article>
  );
};

export const AirbnbHomeMapSection = ({
  lang,
  copy,
  loading,
  properties,
  subAreas,
  selectedSubArea,
  onSubAreaChange,
}) => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia("(min-width: 1024px)").matches,
  );
  const [mapInstance, setMapInstance] = useState(null);
  const [viewportBounds, setViewportBounds] = useState(null);
  const [activePropertyId, setActivePropertyId] = useState(null);
  const [highlightedPropertyId, setHighlightedPropertyId] = useState(null);
  const [sheetHeightVh, setSheetHeightVh] = useState(SHEET_MIN_HEIGHT);
  const dragStateRef = useRef({ isDragging: false, startY: 0, startHeight: SHEET_MIN_HEIGHT });

  const locale = lang === "ar" ? "ar-SA" : "en-US";

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsDesktop(mediaQuery.matches);
    apply();

    mediaQuery.addEventListener("change", apply);
    return () => mediaQuery.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    setMapInstance(null);
    setViewportBounds(null);
  }, [isDesktop]);

  const subAreaOptions = useMemo(() => {
    const safeSubAreas = Array.isArray(subAreas)
      ? subAreas.filter((item) => item && typeof item === "object")
      : [];

    const sorted = [...safeSubAreas].sort((left, right) => {
      const leftLabel = getSubAreaLabel(left, lang);
      const rightLabel = getSubAreaLabel(right, lang);
      return leftLabel.localeCompare(rightLabel, locale);
    });

    return [
      {
        code: "all",
        label: copy.regions.all,
      },
      ...sorted.map((item) => ({
        code: item.code,
        label: getSubAreaLabel(item, lang),
      })),
    ];
  }, [copy.regions.all, lang, locale, subAreas]);

  const mappedProperties = useMemo(
    () =>
      (Array.isArray(properties) ? properties : [])
        .filter((item) => item && typeof item === "object")
        .map((item) => {
        const latitude = toSafeNumber(item.latitude);
        const longitude = toSafeNumber(item.longitude);
        const hasPreciseCoordinates = latitude !== null && longitude !== null;

        const fallback = SUB_AREA_FALLBACK_COORDINATES[item.sub_area_code] || SUB_AREA_FALLBACK_COORDINATES.general;
        const mapLat = hasPreciseCoordinates ? latitude : fallback.lat;
        const mapLng = hasPreciseCoordinates ? longitude : fallback.lng;

          return {
            ...item,
            mapLat,
            mapLng,
            isApproximate: !hasPreciseCoordinates,
            displayPrice: `${Number(item.price || 0).toLocaleString(locale)} ${item.currency || "SAR"}`,
            pinLabel: copy.advancedSearch?.propertyTypes?.[item.property_type] || item.property_type,
            locationLabel: item.location || item.sub_area_code,
          };
        }),
    [copy.advancedSearch?.propertyTypes, locale, properties],
  );

  const mapCenter = useMemo(() => {
    if (selectedSubArea !== "all" && SUB_AREA_FALLBACK_COORDINATES[selectedSubArea]) {
      return SUB_AREA_FALLBACK_COORDINATES[selectedSubArea];
    }

    if (!mappedProperties.length) {
      return MAP_DEFAULT_CENTER;
    }

    const latSum = mappedProperties.reduce((sum, item) => sum + item.mapLat, 0);
    const lngSum = mappedProperties.reduce((sum, item) => sum + item.mapLng, 0);

    return {
      lat: latSum / mappedProperties.length,
      lng: lngSum / mappedProperties.length,
    };
  }, [mappedProperties, selectedSubArea]);

  const handleViewportChange = useCallback((bounds) => {
    if (!bounds) {
      return;
    }

    setViewportBounds({
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
    });
  }, []);

  const viewportProperties = useMemo(
    () =>
      mappedProperties.filter((item) =>
        isInsideBounds(
          {
            lat: item.mapLat,
            lng: item.mapLng,
          },
          viewportBounds,
        ),
      ),
    [mappedProperties, viewportBounds],
  );

  const activeProperty = useMemo(
    () => mappedProperties.find((item) => item.id === activePropertyId) || null,
    [activePropertyId, mappedProperties],
  );

  const focusProperty = useCallback(
    (property) => {
      if (!property) {
        return;
      }

      setHighlightedPropertyId(property.id);
      setActivePropertyId(property.id);
      setSheetHeightVh((prev) => Math.max(prev, 52));

      if (!mapInstance) {
        return;
      }

      const targetZoom = Math.max(mapInstance.getZoom(), 13);
      mapInstance.flyTo([property.mapLat, property.mapLng], targetZoom, {
        animate: true,
        duration: 0.6,
      });
    },
    [mapInstance],
  );

  useEffect(() => {
    if (!mapInstance || !selectedSubArea || selectedSubArea === "all") {
      return;
    }

    const fallback = SUB_AREA_FALLBACK_COORDINATES[selectedSubArea];
    if (!fallback) {
      return;
    }

    mapInstance.flyTo([fallback.lat, fallback.lng], 12, {
      animate: true,
      duration: 0.6,
    });
  }, [mapInstance, selectedSubArea]);

  useEffect(() => {
    if (!mapInstance || selectedSubArea !== "all" || !mappedProperties.length) {
      return;
    }

    const bounds = L.latLngBounds(mappedProperties.map((item) => [item.mapLat, item.mapLng]));
    mapInstance.fitBounds(bounds.pad(0.22), {
      animate: false,
    });
  }, [mapInstance, mappedProperties, selectedSubArea]);

  useEffect(() => {
    if (activePropertyId && !viewportProperties.some((item) => item.id === activePropertyId)) {
      setActivePropertyId(null);
    }

    if (highlightedPropertyId && !viewportProperties.some((item) => item.id === highlightedPropertyId)) {
      setHighlightedPropertyId(null);
    }
  }, [activePropertyId, highlightedPropertyId, viewportProperties]);

  const onSheetPointerDown = (event) => {
    dragStateRef.current = {
      isDragging: true,
      startY: event.clientY,
      startHeight: sheetHeightVh,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onSheetPointerMove = (event) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    const viewportHeight = window.innerHeight || 1;
    const deltaY = dragStateRef.current.startY - event.clientY;
    const nextHeight = dragStateRef.current.startHeight + (deltaY / viewportHeight) * 100;
    setSheetHeightVh(Math.max(SHEET_MIN_HEIGHT, Math.min(SHEET_MAX_HEIGHT, nextHeight)));
  };

  const onSheetPointerUp = (event) => {
    dragStateRef.current.isDragging = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  return (
    <section className="space-y-4" id="properties" data-testid="home-airbnb-map-section">
      <div className="flex flex-wrap items-end justify-between gap-4" data-testid="home-airbnb-map-header">
        <div className="space-y-1">
          <h3 className="text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="home-airbnb-map-title">{copy.mapExperience.title}</h3>
          <p className="text-sm text-stone-600 sm:text-base" data-testid="home-airbnb-map-subtitle">{copy.mapExperience.subtitle}</p>
        </div>

        <span className="rounded-full border border-gold-500/30 bg-white px-5 py-2 text-sm font-medium text-stone-700" data-testid="home-airbnb-map-in-view-count">
          {viewportProperties.length} {copy.mapExperience.inViewLabel}
        </span>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="home-airbnb-map-subarea-filters">
        {subAreaOptions.map((option) => (
          <button
            key={option.code}
            type="button"
            onClick={() => onSubAreaChange(option.code)}
            className={`rounded-full border px-4 py-2 text-xs transition-colors sm:text-sm ${
              selectedSubArea === option.code
                ? "border-gold-500/50 bg-gold-100 text-gold-900"
                : "border-gold-500/20 bg-white text-stone-700 hover:bg-gold-100/50"
            }`}
            data-testid={`home-airbnb-map-subarea-filter-${option.code}`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {isDesktop ? (
        <div className="gap-6 lg:grid lg:grid-cols-[minmax(420px,48%)_minmax(0,52%)]" data-testid="home-airbnb-map-desktop-layout">
          <aside className="sticky top-28 h-[calc(100vh-8.5rem)] overflow-hidden rounded-[1.8rem] border border-gold-500/25 bg-stone-900/95 shadow-2xl" data-testid="home-airbnb-map-desktop-canvas-wrapper">
            <MapContainer
              center={[mapCenter.lat, mapCenter.lng]}
              zoom={MAP_DEFAULT_ZOOM}
              minZoom={7}
              scrollWheelZoom
              className="h-full w-full"
              data-testid="home-airbnb-map-desktop-canvas"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MapViewportBridge onViewportChange={handleViewportChange} onMapReady={setMapInstance} />

              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={createClusterIcon}
                maxClusterRadius={60}
                spiderfyOnMaxZoom
                showCoverageOnHover={false}
                zoomToBoundsOnClick
                disableClusteringAtZoom={16}
              >
                {viewportProperties.map((property) => (
                  <Marker
                    key={property.id}
                    position={[property.mapLat, property.mapLng]}
                    icon={createPricePinIcon({
                      label: property.pinLabel,
                      isActive: property.id === highlightedPropertyId,
                      markerId: property.id,
                    })}
                    eventHandlers={{
                      click: () => {
                        setHighlightedPropertyId(property.id);
                        setActivePropertyId(property.id);
                      },
                    }}
                  />
                ))}
              </MarkerClusterGroup>
            </MapContainer>

            {activeProperty && (
              <div className="pointer-events-none absolute inset-x-4 bottom-4 z-[1000]" data-testid="home-airbnb-map-desktop-preview-wrapper">
                <div className="pointer-events-auto">
                  <PropertyPreview
                    lang={lang}
                    property={activeProperty}
                    copy={copy}
                    testIdPrefix="home-airbnb-map-desktop-preview"
                    layout="desktop-dock"
                  />
                </div>
              </div>
            )}
          </aside>

          <div className="h-[calc(100vh-8.5rem)] overflow-y-auto rounded-[1.8rem] border border-gold-500/20 bg-white/85 p-4" data-testid="home-airbnb-map-desktop-list-panel">
            {loading ? (
              <div className="rounded-2xl border border-dashed border-gold-500/30 bg-white/80 p-8 text-center text-stone-600" data-testid="home-airbnb-map-desktop-loading">
                {copy.properties.loading}
              </div>
            ) : viewportProperties.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gold-500/30 bg-white/80 p-8 text-center text-stone-600" data-testid="home-airbnb-map-desktop-empty">
                {copy.mapExperience.emptyInView}
              </div>
            ) : (
              <div className="space-y-4" data-testid="home-airbnb-map-desktop-list">
                {viewportProperties.map((property) => (
                  <PropertyListCard
                    key={property.id}
                    lang={lang}
                    property={property}
                    copy={copy}
                    isHighlighted={property.id === highlightedPropertyId}
                    onFocus={() => focusProperty(property)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="relative h-[100svh] overflow-hidden rounded-[1.8rem] border border-gold-500/25 bg-stone-900/95 shadow-2xl" data-testid="home-airbnb-map-mobile-layout">
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={MAP_DEFAULT_ZOOM}
            minZoom={7}
            scrollWheelZoom
            className="h-full w-full"
            data-testid="home-airbnb-map-mobile-canvas"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapViewportBridge onViewportChange={handleViewportChange} onMapReady={setMapInstance} />

            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={createClusterIcon}
              maxClusterRadius={60}
              spiderfyOnMaxZoom
              showCoverageOnHover={false}
              zoomToBoundsOnClick
              disableClusteringAtZoom={16}
            >
              {viewportProperties.map((property) => (
                <Marker
                  key={`mobile-${property.id}`}
                  position={[property.mapLat, property.mapLng]}
                  icon={createPricePinIcon({
                    label: property.pinLabel,
                    isActive: property.id === highlightedPropertyId,
                    markerId: `mobile-${property.id}`,
                  })}
                  eventHandlers={{
                    click: () => {
                      setHighlightedPropertyId(property.id);
                      setActivePropertyId(property.id);
                      setSheetHeightVh((prev) => Math.max(prev, 55));
                    },
                  }}
                />
              ))}
            </MarkerClusterGroup>
          </MapContainer>

          <div
            className="absolute inset-x-0 bottom-0 z-[1200] rounded-t-[1.8rem] border border-gold-500/30 bg-white/95 shadow-2xl backdrop-blur-xl"
            style={{ height: `${sheetHeightVh}svh` }}
            data-testid="home-airbnb-map-mobile-bottom-sheet"
          >
            <div className="flex h-full flex-col" data-testid="home-airbnb-map-mobile-bottom-sheet-content">
              <div
                role="button"
                tabIndex={0}
                onPointerDown={onSheetPointerDown}
                onPointerMove={onSheetPointerMove}
                onPointerUp={onSheetPointerUp}
                onPointerCancel={onSheetPointerUp}
                className="cursor-grab touch-none px-4 pb-2 pt-3 active:cursor-grabbing"
                data-testid="home-airbnb-map-mobile-sheet-drag-handle"
              >
                <span className="airbnb-mobile-sheet-handle" data-testid="home-airbnb-map-mobile-sheet-handle-bar" />
                <div className="mt-2 flex items-center justify-between gap-2" data-testid="home-airbnb-map-mobile-sheet-header-row">
                  <p className="text-sm font-semibold text-stone-800" data-testid="home-airbnb-map-mobile-sheet-title">{copy.mapExperience.mobileSheetTitle}</p>
                  <p className="text-xs text-stone-600" data-testid="home-airbnb-map-mobile-sheet-count">
                    {viewportProperties.length} {copy.mapExperience.inViewLabel}
                  </p>
                </div>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-5" data-testid="home-airbnb-map-mobile-sheet-scroll-area">
                {activeProperty && (
                  <PropertyPreview lang={lang} property={activeProperty} copy={copy} testIdPrefix="home-airbnb-map-mobile-preview" />
                )}

                {loading ? (
                  <div className="rounded-2xl border border-dashed border-gold-500/30 bg-white p-6 text-center text-sm text-stone-600" data-testid="home-airbnb-map-mobile-loading">
                    {copy.properties.loading}
                  </div>
                ) : viewportProperties.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gold-500/30 bg-white p-6 text-center text-sm text-stone-600" data-testid="home-airbnb-map-mobile-empty">
                    {copy.mapExperience.emptyInView}
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="home-airbnb-map-mobile-list">
                    {viewportProperties.map((property) => (
                      <div
                        key={`mobile-list-${property.id}`}
                        className={`rounded-2xl border bg-white p-3 ${
                          property.id === highlightedPropertyId ? "border-gold-500/40" : "border-gold-500/20"
                        }`}
                        data-testid={`home-airbnb-map-mobile-list-card-${property.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => focusProperty(property)}
                          className="flex w-full items-center justify-between gap-3 text-start"
                          data-testid={`home-airbnb-map-mobile-focus-button-${property.id}`}
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-stone-900" data-testid={`home-airbnb-map-mobile-title-${property.id}`}>{property.title}</p>
                            <p className="text-xs text-stone-600" data-testid={`home-airbnb-map-mobile-price-${property.id}`}>{property.displayPrice}</p>
                            {hasPositiveCount(property.floors_count) && (
                              <p className="mt-1 text-[11px] text-stone-500" data-testid={`home-airbnb-map-mobile-floors-${property.id}`}>
                                {copy.properties.floorsCount}: {property.floors_count}
                              </p>
                            )}
                            {hasPositiveCount(property.units_count) && (
                              <p className="mt-1 text-[11px] text-stone-500" data-testid={`home-airbnb-map-mobile-units-${property.id}`}>
                                {getUnitsLabel(copy, property.property_type)}: {property.units_count}
                              </p>
                            )}
                          </div>

                          {property.isApproximate && (
                            <Badge variant="outline" className="border-gold-500/40 bg-amber-50 text-[10px] text-stone-700" data-testid={`home-airbnb-map-mobile-approximate-badge-${property.id}`}>
                              {copy.mapExperience.approximateLocation}
                            </Badge>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
